/*
Both sessionStorage or localStorage could be used here.
The sessionStorage doesn't support triggering syncing events on other browser tabs
(not utilized anyway ATM to have same functionality scope for all implemented storages).
The sessionStorage should clean up stored data after the browser tab gets closed.
But the browser's restore-session-on-reopen functionality may clash with this.
Both storage types have no connection with server-side session,
so, if working with server session data, manual cleanups / refreshing
has to be solved anyway.
*/
import { logDebug, logWarning, mainLogger } from '../logger';
import { getUserCheck } from './getUserCheck';
import { isValidStorageTimestamp } from './isValidStorageTimestamp';
const childLogger = mainLogger.createChild('local storage');
export const getStorage = () => typeof window === 'object' && window.localStorage;

/** Returns true if the local storage is available. */
export function isStorageAvailable() {
  const isAvailable = Boolean(getStorage());
  if (!isAvailable) {
    logWarning('Storage is not available.');
  }
  return isAvailable;
}

// TODO -- don't we have some util for this already in some NPM?
function parseValue(serializedData) {
  let parsedValue;
  try {
    parsedValue = JSON.parse(serializedData);
  } catch (error) {
    childLogger.error(error, {
      action: 'Error parsing serialized data.'
    });
  }
  return parsedValue;
}

/** Returns a value identified by the key stored in localStorage. */
export const getStorageItem = async key => new Promise(resolve => {
  const storage = getStorage();
  if (typeof storage !== 'object') {
    resolve(undefined);
    return;
  }
  const data = storage.getItem(key);
  if (!data) {
    logDebug(`Item with key '${key}' wasn't found in storage.`);
    resolve(undefined);
    return;
  }
  const parsedData = parseValue(data);
  if (!parsedData) {
    const error = new Error(`Error getting stored item with key '${key}'.`);
    childLogger.error(error, {
      action: 'getStorageItem'
    });
    resolve(undefined);
    return;
  }
  const {
    savedTimestamp,
    value,
    userCheck
  } = parsedData;
  if (!isValidStorageTimestamp(savedTimestamp)) {
    storage.removeItem(key);
    logDebug(`Stored item with key '${key}' has expired and was deleted.`);
    resolve(undefined);
    return;
  }
  if (userCheck !== getUserCheck()) {
    storage.removeItem(key);
    logDebug(`Item with key '${key}' was stored for different user and was deleted.`);
    resolve(undefined);
    return;
  }
  logDebug(`Item with key ${key} was found in storage.\n\t`, parsedData);
  resolve(value);
});

/** Stores a value under the specified key in localStorage. */
export const setStorageItem = async (key, value) => new Promise(resolve => {
  const storage = getStorage();
  if (typeof storage !== 'object') {
    resolve(false);
    return;
  }
  const data = {
    savedTimestamp: Date.now(),
    value,
    userCheck: getUserCheck()
  };
  try {
    storage.setItem(key, JSON.stringify(data));
    logDebug(`Stored item with key '${key}', value:\n\t`, data);
    resolve(true);
  } catch (error) {
    childLogger.error(error, {
      action: `setStorageItem - storing item with ${key}:`
    });
    resolve(false);
  }
});

/** Removes a value identified by the specified key from localStorage. */
export const removeStorageItem = async key => new Promise(resolve => {
  const storage = getStorage();
  if (typeof storage !== 'object') {
    resolve(false);
    return;
  }
  try {
    storage.removeItem(key);
    logDebug(`Stored item with key '${key}' was removed.`);
    resolve(true);
  } catch (error) {
    childLogger.error(error, {
      action: `removeStorageItem - removing item with ${key}:`
    });
    resolve(false);
  }
});
//# sourceMappingURL=storageImplLocal.js.map